import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "../../lib/queryKeys";
import {
  disablePush,
  enablePush,
  pushState,
  type PushAvailability,
} from "../../lib/push";
import { Button } from "../ui/Button";

/**
 * Push notifications, for the browser you are looking at.
 *
 * Sits inside the notifications panel rather than beside it, because it is not
 * a fifth product -- it is a third way the same notifications reach you, and it
 * answers a question the table above cannot: not *which* things, but *where*.
 *
 * ## Why this is one switch and not a column of tick boxes
 *
 * Push deliberately follows the "Pulse" column. Something worth a bell is worth
 * a buzz -- the push simply is the bell arriving when the tab is shut -- and the
 * grain of the two settings genuinely differs: email is a property of a person,
 * where push is a property of a *browser*, and a person has several. A third
 * column would multiply sixteen switches by every phone and laptop somebody
 * owns to express a preference ("mentions on my phone but not my desktop") that
 * nobody has asked for.
 *
 * ## Every state says something different
 *
 * The failure modes here are all silent and all need different words. A browser
 * with no support, an iPhone that has not installed the app, a permission
 * blocked in browser settings that script is not allowed to ask about again, a
 * server with no keys -- rendering those as one greyed-out switch would leave
 * somebody toggling a control that cannot work and no way to find out why.
 */

/** What the switch, or the sentence in its place, should say. */
const COPY: Record<
  PushAvailability,
  { title: string; blurb: string; action: "enable" | "disable" | null }
> = {
  on: {
    title: "On for this device",
    blurb:
      "Anything ticked under Pulse above will also reach you here when Pulse is closed.",
    action: "disable",
  },
  off: {
    title: "Off for this device",
    blurb:
      "Get notified when Pulse is closed. Your browser will ask permission first.",
    action: "enable",
  },
  "needs-install": {
    title: "Add Pulse to your Home Screen first",
    blurb:
      "On iPhone and iPad, Safari only allows notifications for an installed app. Tap Share, then “Add to Home Screen”, and open Pulse from there.",
    action: null,
  },
  denied: {
    title: "Blocked by your browser",
    blurb:
      "Notifications are turned off for Pulse in your browser settings, and only you can turn them back on there. In Chrome it is the icon at the left of the address bar.",
    action: null,
  },
  unsupported: {
    title: "Not available in this browser",
    blurb:
      "This browser cannot receive push notifications. Pulse will still show them in the bell while it is open.",
    action: null,
  },
  "not-configured": {
    title: "Not set up on this server",
    blurb:
      "Push notifications need VAPID keys configuring. Ask whoever runs Pulse.",
    action: null,
  },
};

export function PushSettings() {
  const queryClient = useQueryClient();

  const state = useQuery({
    queryKey: queryKeys.pushState,
    queryFn: pushState,
    /*
     * Always refetched on mount. Half of this answer is browser state -- a
     * permission revoked in another tab, a subscription the browser rotated --
     * and none of that invalidates a cache, so a stale entry here would show
     * somebody a switch that disagrees with their own browser.
     */
    staleTime: 0,
    refetchOnMount: "always",
  });

  const toggle = useMutation({
    mutationFn: async (next: boolean) => {
      if (next) return enablePush();
      await disablePush();
      return "off" as const;
    },
    /*
     * Not optimistic, unlike the switches above. This one opens a permission
     * prompt the person can dismiss or refuse, so the honest thing is to wait
     * and show what actually happened -- a switch that slid to On and slid back
     * is worse than one that took a moment.
     */
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.pushState }),
  });

  if (state.isLoading) {
    return <p className="text-xs text-muted">Checking this device…</p>;
  }

  const availability = state.data?.availability ?? "unsupported";
  const others = state.data?.otherDevices ?? 0;
  const copy = COPY[availability];

  return (
    <section className="space-y-2 rounded-md border border-line bg-surface p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-xs font-semibold text-content">
            Push notifications
          </h4>
          <p className="text-xs text-muted">{copy.title}</p>
        </div>

        {copy.action ? (
          <Button
            size="sm"
            variant={copy.action === "enable" ? "primary" : "secondary"}
            disabled={toggle.isPending}
            onClick={() => toggle.mutate(copy.action === "enable")}
          >
            {toggle.isPending
              ? "Just a moment…"
              : copy.action === "enable"
                ? "Turn on"
                : "Turn off"}
          </Button>
        ) : null}
      </div>

      <p className="text-xs text-faint">{copy.blurb}</p>

      {/*
        Other devices, when there are any. Worth saying on a desktop that cannot
        do push at all -- "your phone is still signed up" is the difference
        between this panel looking broken and looking correct.
      */}
      {others > 0 ? (
        <p className="text-xs text-faint">
          Also on {others} other {others === 1 ? "device" : "devices"}. Turning
          it off here leaves {others === 1 ? "that one" : "those"} alone.
        </p>
      ) : null}

      {/*
        A refused prompt is not an error and gets no red text -- `enablePush`
        reports it as plain "off". This is for the genuine failures: the network
        died, or the browser threw on subscribe.
      */}
      {toggle.isError ? (
        <p className="text-xs text-red-600">
          Could not change this. Try again, or reload the page.
        </p>
      ) : null}
    </section>
  );
}
